import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'redux-first-history';

import { Col, Container, Row } from 'ui/gridSystem';

import { Button, CustomInput } from 'ui/atoms';
import ProDisplay from 'ui/typography/proDisplay';
import { withMediaQueries } from 'hoc/withMediaQueries';
import validator from 'utils/regex-validator';
import OnBoardingLayout from 'ui/components/OnBoardingLayout';
import routes from 'routes';
import {
  CREATE_USER_ONBOARDING,
  stepsOnboarding,
} from 'redux/actions/onBoarding';
import AgreementCheck from 'ui/components/AgreementCheck';
import {
  ChangePasswordContainer,
  Title,
  Body,
  ButtonWrapper,
  InputWrapper,
} from './style';

const newField = () => ({
  value: undefined,
  valid: true,
});

const initialForm = {
  firstname: newField(),
  lastname: newField(),
  phoneNumber: newField(),
  fiscalCode: newField(),
  userJob: newField(),
};

const CreateUser = ({
  mediaIsPhone,
  createUser,
  pushUrl,
  stepOnBoarding,
  toSendPassword,
  prospectId,
  agreements,
  agreementsModalChecked,
}) => {
  const [form, setForm] = useState(initialForm);

  const handleOnChange = (field, newValue) =>
    setForm(s => {
      const newState = { ...s };
      newState[field].value = newValue;
      return newState;
    });

  const handleOnBlur = (type, field, callback) => {
    setForm(s => {
      const newState = { ...s };
      newState[field].valid = validator[type](form[field].value);
      return newState;
    });

    if (callback) callback();
  };

  const buttonDisabled =
    !Object.values(form).reduce((acc, field) => acc && field.value && field.value.length > 0, true) ||
    typeof form.fiscalCode.valid === 'string'
      ? !!form.fiscalCode.valid
      : !form.fiscalCode.valid;

  const handleCreateUser = () => {
    createUser({
      firstName: form.firstname?.value,
      lastName: form.lastname?.value,
      phoneNumber: form.phoneNumber.value,
      fiscalCode: form.fiscalCode.value,
      userJob: form.userJob.value,
      passwordUser: toSendPassword,
      prospectId,
      persDataAcceptance: (Object.values(agreements.user_clauses) || []).map(
        clause => ({
          authorize: clause.authorize,
          type: clause.type,
          mandatory: clause.mandatory,
        })
      ),
    });
  };

  useEffect(() => {
    if (stepOnBoarding === stepsOnboarding.STEP3) {
      pushUrl(routes.drawProfile.path);
    }
  }, [stepOnBoarding, pushUrl]);

  useEffect(() => {
    if (!prospectId) {
      pushUrl(routes.landingPage.path);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formConfirmed = () => {
    let valid = true;
    if (agreements?.user_clauses) {
      (Object.keys(agreements.user_clauses) || []).forEach(key => (valid = valid && agreements.user_clauses[key]?.valid));
    } else {
      valid = false;
    }
    return valid && agreementsModalChecked;
  };

  return (
    <OnBoardingLayout step={2}>
      <Container>
        <Row>
          <ChangePasswordContainer>
            <Col lg={6} md={6} sm={12}>
              <Body>
                <Title>
                  <ProDisplay
                    type="title"
                    configuration={mediaIsPhone ? { fontSize: '20' } : null}
                  >
                    Manca poco, inserisci gli ultimi dati
                  </ProDisplay>
                </Title>
                <InputWrapper>
                  <CustomInput
                    type="text"
                    placeholder="Nome *"
                    value={form.firstname.value}
                    onChange={val => handleOnChange('firstname', val)}
                    onBlur={() => handleOnBlur('string', 'firstname')}
                    error={
                      typeof form.firstname.valid === 'string'
                        ? form.firstname.valid
                        : !form.firstname.valid
                    }
                  />
                </InputWrapper>
                <InputWrapper>
                  <CustomInput
                    type="text"
                    placeholder="Cognome *"
                    value={form.lastname.value}
                    onChange={val => handleOnChange('lastname', val)}
                    onBlur={() => handleOnBlur('string', 'lastname')}
                    error={
                      typeof form.lastname.valid === 'string'
                        ? form.lastname.valid
                        : !form.lastname.valid
                    }
                  />
                </InputWrapper>
                <InputWrapper>
                  <CustomInput
                    type="text"
                    placeholder="Telefono *"
                    value={form.phoneNumber.value}
                    onChange={val => handleOnChange('phoneNumber', val)}
                    onBlur={() => handleOnBlur('string', 'phoneNumber')}
                    error={
                      typeof form.phoneNumber.valid === 'string'
                        ? form.phoneNumber.valid
                        : !form.phoneNumber.valid
                    }
                  />
                </InputWrapper>
                <InputWrapper>
                  <CustomInput
                    type="text"
                    placeholder="Codice Fiscale *"
                    forceUppercase
                    value={form.fiscalCode.value}
                    onChange={val =>
                      handleOnChange('fiscalCode', val.toUpperCase())
                    }
                    onBlur={() => handleOnBlur('fiscalCode', 'fiscalCode')}
                    error={
                      typeof form.fiscalCode.valid === 'string'
                        ? form.fiscalCode.valid
                        : !form.fiscalCode.valid
                    }
                  />
                </InputWrapper>
                <InputWrapper>
                  <CustomInput
                    type="text"
                    placeholder="Ruolo in azienda *"
                    value={form.userJob.value}
                    onChange={val => handleOnChange('userJob', val)}
                    onBlur={() => handleOnBlur('string', 'userJob')}
                    error={
                      typeof form.userJob.valid === 'string'
                        ? form.userJob.valid
                        : !form.userJob.valid
                    }
                  />
                </InputWrapper>
                <Row>
                  <AgreementCheck validator={formConfirmed} isSimpleUser />
                </Row>
                <ButtonWrapper>
                  <Button
                    type={Button.TYPE.PRIMARY}
                    text="Accedi"
                    disabled={buttonDisabled || !formConfirmed()}
                    onClick={handleCreateUser}
                  />
                </ButtonWrapper>
              </Body>
            </Col>
          </ChangePasswordContainer>
        </Row>
      </Container>
    </OnBoardingLayout>
  );
};

CreateUser.propTypes = {
  // HOC withMediaQueries
  mediaIsPhone: PropTypes.bool.isRequired,

  // HOC (store)
  stepOnBoarding: PropTypes.string,
  toSendPassword: PropTypes.string,
  prospectId: PropTypes.string,
  agreements: PropTypes.object,
  agreementsModalChecked: PropTypes.bool,

  // HOC (connect, dispatch)
  pushUrl: PropTypes.func.isRequired,
  createUser: PropTypes.func.isRequired,
};

export default connect(
  ({
    onBoarding: { stepOnBoarding, toSendPassword, prospectId },
    signup: { agreements, agreementsModalChecked },
  }) => ({
    stepOnBoarding,
    toSendPassword,
    prospectId,
    agreements,
    agreementsModalChecked,
  }),
  dispatch => ({
    pushUrl: url => dispatch(push(url)),
    createUser: payload =>
      dispatch({ type: CREATE_USER_ONBOARDING._REQUEST, payload }),
  })
)(withMediaQueries(CreateUser));
